import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

/**
 * 2.0X — background enrichment awareness.
 *
 * Owns every in-flight enrichment OUTSIDE the panel that started it, mounted around
 * AppLayout exactly as `SearchProvider` is, so a run survives closing the panel, going back
 * to the results and navigating to another route. The panel and the results list only READ
 * from here; neither owns a job, and neither may start one twice.
 *
 * BG1 — THE RUN LIVES ON THE SERVER
 *
 * `startEnrichment` POSTs /leads/{id}/enrichment-jobs, which returns at once with a job the
 * server runs. `followJob` then polls that job and, when it completes, reads the card with the
 * ordinary GET /leads/{id}/enrichment and settles it through the same `finish()` and the same
 * toasts as before. Everything else — the jobs map, `isEnriching`, `takeResult`, the
 * consumers — is written against the STATE, and none of it changed.
 *
 * Because the server owns the run, a refresh or a closed tab loses nothing: on mount the
 * provider reads the company's active jobs once and follows each of them again. The server
 * also never runs one lead twice at a time: a second start gets back the job already running.
 *
 * Polling contract, the same as `SearchContext`:
 *   - hits GET /enrichment-jobs/{jobId}, every 3000 ms, one loop per lead
 *   - stops on "completed", "failed" and "interrupted"
 *   - a network error or a 5xx does NOT stop polling; a 4xx answer does
 *   - logout and unmount stop every loop
 */

const EnrichmentContext = createContext(null);

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;

// BG1 — the SearchContext cadence.
export const POLL_INTERVAL_MS = 3000;

// Mirrors the backend's _ENRICHED_INDICATOR_FIELDS. Frontend-only, and only to word the
// toast: "ran but found nothing" is a different message from "ran and filled the card".
const INDICATOR_FIELDS = [
  ['shop', 'contact_email'],
  ['shop', 'contact_phone'],
  ['shop', 'contact_page_url'],
  ['company_data', 'legal_company_name'],
  ['company_data', 'vat'],
  ['company_data', 'address_street'],
  ['company_data', 'address_number'],
  ['company_data', 'postal_code'],
  ['company_data', 'city'],
  ['company_data', 'country'],
  ['company_data', 'imprint_url'],
];

export const hasAnyIndicator = (data) =>
  INDICATOR_FIELDS.some(([section, field]) => data?.[section]?.[field]?.value);

const authConfig = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A 4xx is an ANSWER (not found, not allowed) and ends the loop. No response at all, or a
// 5xx, is a hiccup, and the next tick asks again.
const isAnswer = (error) => {
  const status = error?.response?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
};

export const EnrichmentProvider = ({ children }) => {
  const { token } = useAuth();

  // leadId -> { status: 'running' | 'done' | 'error', data, error, name, finishedAt }
  // One map, one owner. A job is keyed by lead id, so several leads enrich in parallel
  // without a queue and without any of them knowing about the others.
  const [jobs, setJobs] = useState({});

  // The guard against a double-fired run, and deliberately a REF rather than the state
  // above: two clicks in the same tick would both read the same stale `jobs` and both pass.
  // A ref is written synchronously, so the second click loses. Each run costs up to four
  // SerpApi requests on a 250/month plan — a duplicate is real money, not a cosmetic bug.
  // BG1: the server now refuses a second run of an active lead as well; this stays the
  // first line, and it is also what keeps the buttons disabled.
  const runningRef = useRef(new Set());

  // BG1 — the polling loops outlive any one render. They read the current token through a
  // ref, and stop when the session they started in ends: logout bumps `sessionRef`, and a
  // real unmount clears `mountedRef`.
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  const sessionRef = useRef(0);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // One-shot pickup of the server's active jobs per login.
  const adoptedRef = useRef(false);

  const isEnriching = useCallback((leadId) => runningRef.current.has(leadId), []);

  const track = useCallback((leadId, name) => {
    runningRef.current.add(leadId);
    setJobs((current) => ({
      ...current,
      [leadId]: { status: 'running', name },
    }));
  }, []);

  const finish = useCallback((leadId, name, patch) => {
    runningRef.current.delete(leadId);
    setJobs((current) => ({
      ...current,
      [leadId]: { ...current[leadId], name, finishedAt: Date.now(), ...patch },
    }));
  }, []);

  // Settles a run as an error. Returns the outcome, so the callers can hand it on.
  const fail = useCallback((leadId, name, error, { message, quiet } = {}) => {
    finish(leadId, name, { status: 'error', error });
    if (!quiet) {
      toast.error(message || `${name}: enrichment failed`, { position: 'bottom-right' });
    }
    return 'error';
  }, [finish]);

  /**
   * BG1 — follow one server job until it ends, then settle it exactly as the synchronous
   * run was settled. Whoever started the job — this tab, another tab, a colleague, or a
   * session before a refresh — the ending is the same.
   *
   * Resolves to 'done' or 'error', or to null when the session ended first. `quiet`
   * settles without the per-lead toasts; bulk enrichment reports once, for the whole run.
   */
  const followJob = useCallback(async (leadId, jobId, name, { quiet = false } = {}) => {
    const session = sessionRef.current;
    const live = () => mountedRef.current && sessionRef.current === session;

    while (live()) {
      let job = null;
      try {
        const response = await axios.get(
          `${API_URL}/enrichment-jobs/${jobId}`,
          authConfig(tokenRef.current)
        );
        job = response.data;
      } catch (error) {
        if (!live()) return null;
        if (isAnswer(error)) {
          return fail(leadId, name, error, { quiet });
        }
      }
      if (!live()) return null;

      if (job?.status === 'completed') {
        try {
          // The card is read where it always lives; the job never carries it.
          const response = await axios.get(
            `${API_URL}/leads/${leadId}/enrichment`,
            authConfig(tokenRef.current)
          );
          if (!live()) return null;
          finish(leadId, name, { status: 'done', data: response.data });

          // Bottom of the screen, per-toast: the global <Toaster> is top-right and is shared
          // with every other message in the app, so it is not moved for this one.
          if (!quiet) {
            if (hasAnyIndicator(response.data)) {
              toast.success(`${name} enriched`, { position: 'bottom-right' });
            } else {
              toast.info(`${name}: no public data found`, { position: 'bottom-right' });
            }
          }
          return 'done';
        } catch (error) {
          if (!live()) return null;
          if (isAnswer(error)) {
            return fail(leadId, name, error, { quiet });
          }
          // The card is saved on the server; only this read failed. Read it again next tick.
        }
      } else if (job?.status === 'failed') {
        return fail(leadId, name, job.error, { quiet });
      } else if (job?.status === 'interrupted') {
        // Closed by a server restart. It is never re-run by itself: the user decides.
        return fail(leadId, name, job.error, {
          quiet, message: `${name}: enrichment interrupted. Please run it again.`,
        });
      }

      await wait(POLL_INTERVAL_MS);
    }
    return null;
  }, [finish, fail]);

  /**
   * Start one lead's enrichment as a server job and follow it to its end. The panel and bulk
   * enrichment both start runs here, so every run goes through the server's active-job guard.
   *
   * Resolves to 'done' or 'error' once the run is settled. It resolves to null when nothing
   * was started, because the lead is already enriching here, or when the session ended first.
   */
  const startEnrichment = useCallback(async (lead, { quiet = false } = {}) => {
    const leadId = lead?.id;
    if (!leadId || runningRef.current.has(leadId)) return null;

    const name = lead.shop_name || lead.website || 'this lead';
    track(leadId, name);
    const session = sessionRef.current;

    let job;
    try {
      // BG1 — returns at once. When this lead already has an active job, the server hands
      // that job back instead of starting a second run, so both end up following one job.
      const response = await axios.post(
        `${API_URL}/leads/${leadId}/enrichment-jobs`,
        {},
        authConfig(tokenRef.current)
      );
      job = response.data;
    } catch (error) {
      if (sessionRef.current !== session) return null;
      return fail(leadId, name, error, { quiet });
    }
    if (sessionRef.current !== session) return null;
    return followJob(leadId, job.id, name, { quiet });
  }, [track, fail, followJob]);

  // BG1 — pick up the runs a refresh or a closed tab left behind. A ONE-TIME read per login,
  // never a poll: the server lists the company's queued and running jobs, and each one is
  // followed as if this tab had started it. Nothing is started, so nothing is paid twice.
  useEffect(() => {
    if (!token || adoptedRef.current) return;
    adoptedRef.current = true;
    const session = sessionRef.current;

    axios.get(`${API_URL}/enrichment-jobs`, { ...authConfig(token), params: { active: true } })
      .then((response) => {
        if (!mountedRef.current || sessionRef.current !== session) return;
        (response.data || []).forEach((job) => {
          const leadId = job?.lead_id;
          if (!leadId || runningRef.current.has(leadId)) return;
          const name = job.shop_name || job.website || 'this lead';
          track(leadId, name);
          followJob(leadId, job.id, name);
        });
      })
      .catch(() => {});
  }, [token, track, followJob]);

  // Logout / token loss: stop every loop and drop everything, so nothing leaks into the next
  // session. The jobs themselves keep running on the server.
  useEffect(() => {
    if (token) return;
    sessionRef.current += 1;
    runningRef.current = new Set();
    adoptedRef.current = false;
    setJobs({});
  }, [token]);

  /**
   * The finished result for a lead, handed over ONCE.
   *
   * The panel calls this on mount so a run that completed while it was closed is applied
   * without re-running anything, and again when a job it is watching finishes. Consuming
   * clears the entry so reopening the panel a second time falls back to the ordinary GET
   * rather than replaying a stale payload over fields the user may since have edited.
   */
  const takeResult = useCallback((leadId) => {
    const job = jobs[leadId];
    if (!job || job.status !== 'done') return null;
    setJobs((current) => {
      const next = { ...current };
      delete next[leadId];
      return next;
    });
    return job.data;
  }, [jobs]);

  const value = { jobs, isEnriching, startEnrichment, takeResult };

  return (
    <EnrichmentContext.Provider value={value}>
      {children}
    </EnrichmentContext.Provider>
  );
};

export const useEnrichmentTracker = () => {
  const context = useContext(EnrichmentContext);
  if (!context) {
    throw new Error('useEnrichmentTracker must be used within an EnrichmentProvider');
  }
  return context;
};
